<script setup>
import {
    aboutCutFadeIn,
    newsCutFadeIn,
    gplayCutFadeIn,
    experienceCutFadeIn,
    headerChangeColor,
} from "@/lib/gsap-home";
import { killScrollTrigger } from "@/lib/gsap-core";
import { gsap } from "gsap";

import { htmlPurify } from "@/lib/utils.js";

import { AnimationFrame } from "@/lib/rqa-polify";

import { Swiper, SwiperSlide } from "swiper/vue";
import { EffectCards } from "swiper/modules";
import "swiper/css";
import "swiper/css/effect-cards";

import { useGtag } from "vue-gtag-next";

// 目前語系
const { locale } = useI18n();
// 取得 gtag
const { event } = useGtag();
// 取得頁面資料
const { fetchHomePage, fetchMedias, fetchShorts } = useFetchAPI();
// 設定 SEO Meta
const { setMeta, setFocusNewsSchemaOrgVideo } = useMeta();
// 停止 loading 畫面
const { stopLoading } = useAppState();
// 外部連結 modal
const { openModal } = useExternalLinkModal();
// 視窗大小
const { width, height, isTablet, isDesktop, screenSize } = useWindowSize();
// 目前模式
const { mode, setHeaderMode, setMode } = useMode();
// 取得 nuxtApp
const nuxtApp = useNuxtApp();

const pageMounted = ref(false);
const animationDelay = 0.03;
const animationStart = 0.0;

const delayList = [
    [0, 1, 2, 3, 3, 4, 4],
    [5, 6, 7, 8, 8],
    [9, 10, 11, 12, 13],
];

// data
const contentData = computed(() => {
    return (
        nuxtApp?.payload?.data?.["fetchPage-home"]?.[locale.value] ||
        nuxtApp?.payload?.data?.["fetchPage-home"]?.zh
    );
});

if (!contentData.value) await fetchHomePage();
const content = computed(() => contentData?.value?.meta_box);

const focusNewsData = computed(() => content?.value?.focusNews?.data || []);
const brandsData = computed(() => content?.value?.about?.data || []);
const newsData = computed(() => content?.value?.news?.data || []);
const gplayData = computed(() => content?.value?.gplay?.data || []);
const experienceData = computed(() => {
    const arr = [];
    if (content?.value?.experiences?.data)
        arr.push({ ...content?.value?.experiences?.data });
    if (content?.value?.experiences?.data2)
        arr.push({ ...content.value?.experiences?.data2 });
    if (content?.value?.experiences?.data3)
        arr.push({ ...content.value?.experiences?.data3 });
    return arr;
});

const filteredBrandsData = computed(() => {
    return brandsData.value.filter(
        (item) =>
            item?.meta_box?.brand?.easterEgg &&
            item?.meta_box?.brand?.easterEggSvg,
    );
});

const processedFocusNewsData = computed(() => {
    const arr = [...focusNewsData.value, ...focusNewsData.value];
    return arr.slice(0, 6);
});

const leftFocusNewsData = computed(() => {
    const arr = [...focusNewsData.value, ...focusNewsData.value];
    return arr.slice(6);
});

const gplayDataIds = computed(() => {
    return gplayData.value.map((item) => item.id);
});

const imagesIds = computed(() => {
    return [
        ...experienceData.value.map((item) => item?.image?.[0]),
        contentData?.value?.featured_media,
    ]
        .filter((id) => id !== undefined && id !== null)
        .filter((id, index, array) => array.indexOf(id) === index);
});
const [{ data: imagesData }, { data: shortsData }] = await Promise.all([
    fetchMedias(imagesIds.value, "home-images"),
    fetchShorts(gplayDataIds.value, "home-gplay"),
]);

// 將取得的 shortsData 依 id 建立索引，方便快速對應
const fetchedShortById = new Map();
if (shortsData.value && Array.isArray(shortsData.value)) {
    shortsData.value.forEach((item) => {
        if (item && typeof item.id !== "undefined") {
            fetchedShortById.set(item.id, item);
        }
    });
}

// 依各語系，替換對應的 gplay 資料，並保留原本的 featured_media
const processedGplayData = computed(() => {
    if (gplayData.value && Array.isArray(gplayData.value)) {
        return gplayData.value.map((originalItem) => {
            const updated = fetchedShortById.get(originalItem?.id);
            if (updated) {
                const preservedFeaturedMedia = originalItem?.featured_media;
                return {
                    ...updated,
                    featured_media: preservedFeaturedMedia,
                };
            }
            return originalItem;
        });
    } else return gplayData.value;
});

let tls = [],
    swipers = [];

// hero cut
const showEggTitle = ref(false);
const isGlobeReady = ref(false);
const heroRef = ref(null);
const globeContainerRef = ref(null);
const globeRef = ref(null);

if (import.meta.client) {
    watch(mode, (newVal, oldVal) => {
        if (newVal === "egg") {
            setHeaderMode("dark");
            setTimeout(() => {
                showEggTitle.value = true;
            }, 500);
        } else if (newVal === "normal" && oldVal === "egg") {
            setHeaderMode("light");
            setTimeout(() => {
                showEggTitle.value = false;
            }, 300);
        }
    });
}

const onGlobeReady = async () => {
    if (!import.meta.client) return;
    // loading 畫面消失
    stopLoading();
    // 啟動滾動動畫
    scrollAnimation();
};

// Focus cut
const focusNewsRef = ref(null);
const focusSliderRef = ref(null);
const focusSwiper = ref(null);
const focusSwiperProgress = ref(0);
const focusTextSwiper = ref(null);
const focusTitleRefs = ref([]);
const focusSwiperEnabled = ref(false);
const focusSwiperActiveSlide = ref(0);
const isFocusGamaGlobeShow = ref(false);
const focusGamaGlobe = ref(null);
const arrowHeight = ref(0);
const cardWidth = ref(0);

const mockOffsetX = ref(0);
const accumulatedOffsetX = ref(0);
const mockCarouselBlockRef = ref(null);
const mockSlides = ref([]);
const hasInitMockSwiper = ref(false);
const updateTimes = ref(0);
const focusScrollVelocity = ref(1);
const clampSpeed = computed(() => {
    const speed = (1 / Math.abs(focusScrollVelocity.value)) * 400000;
    return speed < 100 ? 0 : Math.min(400, speed);
});

const initMockSwiper = () => {
    if (hasInitMockSwiper.value) return;
    if (!focusSliderRef.value) return;
    if (!mockCarouselBlockRef.value) return;

    const slides = focusSwiper.value.slides;
    const clientRect = slides[1].getBoundingClientRect();
    const slideWidth = clientRect.width;
    const slideGap = slides[2].getBoundingClientRect().left - clientRect.right;

    mockOffsetX.value = slideWidth + slideGap;
    mockSlides.value.forEach((slide) => {
        slide.style.width = `${slideWidth}px`;
        slide.style.marginRight = `${slideGap}px`;
    });

    const left = mockCarouselBlockRef.value.getBoundingClientRect().left;
    const slideLeft = slides[5].getBoundingClientRect().left;

    mockCarouselBlockRef.value.style.left = `${
        slideLeft - left + mockOffsetX.value
    }px`;
    mockCarouselBlockRef.value.style.opacity = 1;
    mockCarouselBlockRef.value.style.pointerEvents = "auto";
    hasInitMockSwiper.value = true;
};

const updateMockSwiper = (direction, times) => {
    if (!mockCarouselBlockRef.value) return;

    if (direction === "next") {
        if (updateTimes.value > times) return;
        accumulatedOffsetX.value += mockOffsetX.value;
        mockCarouselBlockRef.value.style.transitionDuration = `${clampSpeed.value}ms`;
        mockCarouselBlockRef.value.style.transform = `translateX(-${accumulatedOffsetX.value}px)`;
        updateTimes.value++;
    } else {
        if (updateTimes.value < times) return;
        accumulatedOffsetX.value -= mockOffsetX.value;
        mockCarouselBlockRef.value.style.transitionDuration = `${clampSpeed.value}ms`;
        mockCarouselBlockRef.value.style.transform = `translateX(-${accumulatedOffsetX.value}px)`;
        updateTimes.value--;
    }
};

const focusSwiperInit = (swiper) => {
    if (!import.meta.client) return;

    focusSwiper.value = swiper;

    if (width.value >= 1270) focusSwiper.value.detachEvents();
    else focusSwiper.value.allowTouchMove = false;

    arrowHeight.value = focusSliderRef.value.getBoundingClientRect().height;
    cardWidth.value = focusSwiper.value.slides[0].getBoundingClientRect().width;
    swipers.push(focusSwiper.value);
};

const handleVideoForActiveSlide = (swiper) => {
    if (!focusSwiperEnabled.value) return;

    // 找到所有的影片元素
    const slides = swiper.slides;
    for (let i = 0; i < slides.length; i++) {
        const slide = slides[i];
        const videoEl = slide.querySelector("video");

        if (videoEl) {
            // active slide
            if (i === swiper.activeIndex) {
                if (
                    !videoEl.src ||
                    videoEl.getAttribute("data-no-hydration") === "src"
                ) {
                    // 如果影片尚未載入，載入並移除 data-no-hydration 屬性
                    videoEl.src = videoEl.getAttribute("data-src");
                    videoEl.removeAttribute("data-no-hydration");
                    videoEl.loop = true;
                    videoEl.muted = true;
                    videoEl.playsInline = true;
                }
                // 播放影片
                videoEl.classList.remove("opacity-0");
                videoEl.play();
            } else {
                // inactive slide
                if (videoEl.src && !videoEl.getAttribute("data-no-hydration")) {
                    // 暫停影片
                    videoEl.pause();
                    videoEl.classList.add("opacity-0");
                }
            }
        }
    }
};

const focusSwiperSlideChange = (swiper) => {
    if (!import.meta.client) return;

    focusSwiperActiveSlide.value = swiper.realIndex;

    if (focusTextSwiper.value) {
        focusTextSwiper.value.slideToLoop(focusSwiperActiveSlide.value);
        if (swiper.previousRealIndex !== swiper.realIndex) {
            focusTitleRefs.value[swiper.realIndex].animate({
                alpha: 1,
                duration: 0.5,
                stagger: 0.02,
            });
        }
    }

    // 處理影片的載入和播放
    if (swiper.slides && swiper.slides.length > 0) {
        handleVideoForActiveSlide(swiper);
    }
};

if (import.meta.client) {
    watch(focusSwiperProgress, (newVal, oldVal) => {
        if (!focusSwiper.value) return;

        if (newVal > oldVal) {
            if (oldVal === 0) initMockSwiper();
            focusSwiper.value.slideNext(clampSpeed.value, false);
            updateMockSwiper("next", oldVal);
        } else if (newVal < oldVal) {
            focusSwiper.value.slidePrev(clampSpeed.value, false);
            updateMockSwiper("prev", oldVal);
        }
    });
}

const handleSetTransition = (swiper, transition) => {
    if (!import.meta.client) return;

    for (let i = 0; i < swiper.slides.length; i++) {
        const slide = swiper.slides[i];
        slide.style.transition = `${transition}ms`;
    }
};

const focusTextSwiperInit = (swiper) => {
    if (!import.meta.client) return;

    focusTextSwiper.value = swiper;
    focusTextSwiper.value.detachEvents();
    swipers.push(focusTextSwiper.value);
};

// About cut
const aboutRef = ref(null);
const aboutTitleRef = ref(null);
const aboutBrandRef = ref(null);
const aboutGamaGlobeRef = ref(null);
const aboutGamaGlobe = ref(null);
const shuffledBrands = ref([]);

// 隨機排序子品牌
const initShuffledBrands = () => {
    // 提取第一個元素
    const firstItem = brandsData.value[0];
    // 獲取剩餘元素並進行隨機排序
    const otherItems = [...brandsData.value.slice(1)];
    for (let i = otherItems.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [otherItems[i], otherItems[j]] = [otherItems[j], otherItems[i]];
    }
    // 重新組合並設置結果
    shuffledBrands.value = [firstItem, ...otherItems];
};

// News cut
const colors = ["#00c08b", "#ffb71b", "#0073e6"];
const newsRef = ref(null);
const newsTitleRef = ref(null);
const newsGamaGlobe = ref(null);
const isMouseFollow = ref(false);

const lastMousePosition = ref({ x: 0, y: 0 });
const targetPosition = ref({ x: 0, y: 0 });
const currentPosition = ref({ x: 0, y: 0 });
let animationFrameId = null;
let debounceTimer = null;

let animation;
if (import.meta.client) {
    animation = new AnimationFrame();
}

// debounce
const handleMouseMove = (e) => {
    if (!isMouseFollow.value) return;

    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
        if (newsGamaGlobe.value) {
            const moveX = e.clientX;
            const moveY = e.pageY - newsRef.value.offsetTop;

            targetPosition.value = { x: moveX, y: moveY };
            lastMousePosition.value = { x: moveX, y: moveY };

            if (animation.get_isStop()) {
                animation.start(30, animatePosition);
            }
        }
    }, 10);
};

const updateGlobePosition = (x, y) => {
    if (newsGamaGlobe.value) {
        if (newsGamaGlobe.value.ballRef && newsGamaGlobe.value.ballRef.$el) {
            newsGamaGlobe.value.ballRef.$el.style.transform = `translate(calc(${x}px - 50vw), calc(${y}px  + 50%))`;
            newsGamaGlobe.value.ballRef.$el.style.transition =
                "transform 0.2s ease-out";
        } else if (newsGamaGlobe.value.$el) {
            newsGamaGlobe.value.$el.style.transform = `translate(calc(${x}px - 50vw), calc(${y}px  + 50%))`;
            newsGamaGlobe.value.$el.style.transition =
                "transform 0.2s ease-out";
        }
    }
};

const animatePosition = () => {
    if (!import.meta.client) return;
    if (!isMouseFollow.value) return;

    currentPosition.value.x +=
        (targetPosition.value.x - currentPosition.value.x) * 0.2;
    currentPosition.value.y +=
        (targetPosition.value.y - currentPosition.value.y) * 0.1;

    updateGlobePosition(currentPosition.value.x, currentPosition.value.y);
};

// Gplay cut
const gplayRef = ref(null);
const gplayTitleRef = ref(null);
const gplaySwiper = ref(null);
const gplayGamaGlobeRef = ref(null);
const gplayGamaGlobe = ref(null);

const gplaySwiperInit = (swiper) => {
    if (!import.meta.client) return;

    gplaySwiper.value = swiper;
    if (width.value >= 640) gplaySwiper.value.detachEvents();
    swipers.push(gplaySwiper.value);
};

const gplaySwiperSlideChange = (swiper) => {
    if (!import.meta.client) return;
    if (!pageMounted.value) return;

    if (swiper.slides && swiper.slides.length > 0) {
        const slides = swiper.slides;
        for (let i = 0; i < slides.length; i++) {
            const slide = slides[i];
            const videoEl = slide.querySelector("video");

            if (videoEl) {
                if (i === swiper.activeIndex) {
                    if (
                        !videoEl.src ||
                        videoEl.getAttribute("data-no-hydration") === "src"
                    ) {
                        // 如果影片尚未載入，載入並移除 data-no-hydration 屬性
                        videoEl.src = videoEl.getAttribute("data-src");
                        videoEl.removeAttribute("data-no-hydration");
                        videoEl.loop = true;
                        videoEl.muted = true;
                        videoEl.playsInline = true;
                    }
                    // 播放影片
                    videoEl.classList.remove("opacity-0");
                    videoEl.play();
                } else {
                    // inactive slide
                    if (
                        videoEl.src &&
                        !videoEl.getAttribute("data-no-hydration")
                    ) {
                        // 暫停影片
                        videoEl.pause();
                        videoEl.classList.add("opacity-0");
                    }
                }
            }
        }
    }
};

// Experience cut
const experienceRef = ref(null);
const experienceTitleRef = ref(null);
const experienceSwiper = ref(null);
const experienceSlidesRefs = ref([]);
const experienceActiveSlide = ref(0);
const experienceGamaGlobeRef = ref(null);
const experienceGamaGlobe = ref(null);

const experienceSwiperInit = (swiper) => {
    if (!import.meta.client) return;

    experienceSwiper.value = swiper;
    swipers.push(experienceSwiper.value);
};

const experienceSwiperSlideChange = (swiper) => {
    if (!import.meta.client) return;
    experienceActiveSlide.value = swiper.realIndex;
};

// ESG cut
const esgRef = ref(null);
const esgMobileTitleRef = ref(null);
const esgDesktopTitleRef = ref(null);
const esgMobileSubtitleRef = ref(null);
const esgDesktopSubtitleRef = ref(null);
const esgEarthContainerRef = ref(null);
const esgContentRef = ref(null);

const scrollAnimation = () => {
    const focusCutObject = {
        trigger: focusNewsRef.value,
        slider: focusSliderRef.value,
        slidesLength: focusNewsData.value.length,
        swiper: focusSwiper.value,
        swiperProgress: focusSwiperProgress,
        scrollVelocity: focusScrollVelocity,
        callback: () => {
            if (width.value >= 1270 && focusSwiper.value) {
                isFocusGamaGlobeShow.value = false;
            }
            setTimeout(
                () => {
                    if (focusSwiper.value && width.value < 1270)
                        focusSwiper.value.allowTouchMove = false;
                    focusSwiperEnabled.value = false;
                    // 暫停目前影片
                    if (focusSwiper.value && focusSwiper.value.slides) {
                        for (
                            let i = 0;
                            i < focusSwiper.value.slides.length;
                            i++
                        ) {
                            const videoEl =
                                focusSwiper.value.slides[i].querySelector(
                                    "video",
                                );
                            if (videoEl && videoEl.src) {
                                videoEl.pause();
                                videoEl.classList.add("opacity-0");
                            }
                        }
                    }
                },
                width.value >= 1270 ? 400 : 0,
            );
            focusTitleRefs.value[0]?.set();
        },
        reverseCallback: () => {
            focusSwiperEnabled.value = true;
            if (focusSwiper.value && width.value < 1270)
                focusSwiper.value.allowTouchMove = true;
            isFocusGamaGlobeShow.value = true;
            // 播放目前影片
            if (focusSwiper.value && focusSwiper.value.slides) {
                handleVideoForActiveSlide(focusSwiper.value);
            }
            // 標題淡入
            focusTitleRefs.value[0]?.animate({
                alpha: 1,
                duration: 0.5,
                stagger: 0.02,
            });
        },
    };

    const { heroTl, focusTl } = globeRef.value.heroFadeIn(
        () => (pageMounted.value = true),
        focusCutObject,
    );
    tls.push(heroTl, focusTl);

    setTimeout(() => {
        if (aboutRef.value) {
            // 關於橘子 cut 進場
            // aboutGamaGlobe 出現
            // 關於橘子文字卡拉OK效果
            // 子品牌橫向捲軸（桌機限定）
            const aboutTl = aboutCutFadeIn(
                focusNewsRef.value,
                aboutTitleRef.value,
                aboutBrandRef.value,
                aboutGamaGlobeRef.value,
                aboutRef.value,
            );
            tls.push(aboutTl);
        }
    }, 0);

    setTimeout(() => {
        if (newsRef.value) {
            // 新聞 cut 進場
            // 新聞 cut 標題出現
            // aboutGamaGlobe 消失
            // newsGamaGlobe 隨滑鼠移動
            const newsTl = newsCutFadeIn(
                aboutRef.value,
                newsTitleRef.value,
                newsRef.value,
                () => {
                    if (width.value >= 1270) {
                        isMouseFollow.value = true;
                    }
                },
                () => {
                    if (width.value >= 1270) {
                        isMouseFollow.value = false;
                        animation.stop();
                    }
                },
            );
            tls.push(newsTl);
        }
    }, 0);

    setTimeout(() => {
        if (gplayRef.value) {
            // 橘刻播放 cut 進場
            // 橘刻播放 cut 標題出現
            // 卡牌隨滾動替換（桌機、平板限定）
            // ESG cut 3D 地球準備
            // 取消 newsGamaGlobe 隨滑鼠移動
            const gplayTl = gplayCutFadeIn(
                newsRef.value,
                gplayTitleRef.value,
                gplaySwiper,
                gplayGamaGlobeRef.value,
                gplayRef.value,
                () => {
                    if (width.value >= 640 && width.value < 1270)
                        if (esgEarthContainerRef.value)
                            esgEarthContainerRef.value.style.width = `${esgEarthContainerRef.value.getBoundingClientRect().height}px`;
                    if (
                        esgEarthContainerRef.value &&
                        globeRef.value &&
                        globeRef.value.canvas
                    ) {
                        esgEarthContainerRef.value.appendChild(
                            globeRef.value.canvas,
                        );
                        globeRef.value.setEarthMode();
                    }

                    gplaySwiperSlideChange(gplaySwiper.value);
                },
                () => {
                    if (width.value >= 1270) {
                        isMouseFollow.value = true;
                        animation.start(30, animatePosition);
                    }
                    if (
                        globeContainerRef.value &&
                        globeRef.value &&
                        globeRef.value.canvas
                    ) {
                        globeContainerRef.value.insertBefore(
                            globeRef.value.canvas,
                            globeContainerRef.value.firstChild,
                        );
                        globeRef.value.setBackMode();
                    }

                    // 暫停目前影片
                    if (gplaySwiper.value && gplaySwiper.value.slides) {
                        for (
                            let i = 0;
                            i < gplaySwiper.value.slides.length;
                            i++
                        ) {
                            const videoEl =
                                gplaySwiper.value.slides[i].querySelector(
                                    "video",
                                );
                            if (videoEl && videoEl.src) videoEl.pause();
                        }
                    }
                },
                () => {
                    if (width.value >= 1270) {
                        isMouseFollow.value = false;
                        animation.stop();
                    }
                },
            );
            tls.push(gplayTl);
        } else {
            const gplayTl = gplayCutFadeIn(
                null,
                null,
                null,
                null,
                aboutRef.value,
                () => {
                    if (width.value >= 640 && width.value < 1270)
                        if (esgEarthContainerRef.value)
                            esgEarthContainerRef.value.style.width = `${esgEarthContainerRef.value.getBoundingClientRect().height}px`;
                    if (
                        esgEarthContainerRef.value &&
                        globeRef.value &&
                        globeRef.value.canvas
                    ) {
                        esgEarthContainerRef.value.appendChild(
                            globeRef.value.canvas,
                        );
                        globeRef.value.setEarthMode();
                    }
                },
                () => {
                    if (
                        globeContainerRef.value &&
                        globeRef.value &&
                        globeRef.value.canvas
                    ) {
                        globeContainerRef.value.insertBefore(
                            globeRef.value.canvas,
                            globeContainerRef.value.firstChild,
                        );
                        globeRef.value.setBackMode();
                    }
                },
                () => {},
            );
            tls.push(gplayTl);
        }
    }, 0);

    let experienceTl, esgTl;
    setTimeout(() => {
        if (experienceRef.value) {
            // 立即體驗 cut 進場
            // 立即體驗 cut 標題出現
            experienceTl = experienceCutFadeIn(
                gplayRef.value,
                experienceTitleRef.value,
                width.value >= 1270
                    ? experienceSlidesRefs.value
                    : experienceSwiper.value.slides,
                experienceGamaGlobeRef.value,
                experienceRef.value,
            );
            tls.push(experienceTl);

            // ESG cut 進場
            // 3D 地球出現
            // ESG cut 標題、副標、內文出現
            esgTl = globeRef.value.scrollToShowEarth(
                experienceRef.value,
                [esgMobileTitleRef.value, esgDesktopTitleRef.value],
                [esgMobileSubtitleRef.value, esgDesktopSubtitleRef.value],
                esgContentRef.value,
                esgRef.value,
            );
            tls.push(esgTl);
        } else {
            // ESG cut 進場
            // 3D 地球出現
            // ESG cut 標題、副標、內文出現
            esgTl = globeRef.value.scrollToShowEarth(
                gplayRef.value,
                [esgMobileTitleRef.value, esgDesktopTitleRef.value],
                [esgMobileSubtitleRef.value, esgDesktopSubtitleRef.value],
                esgContentRef.value,
                esgRef.value,
            );
            tls.push(esgTl);
        }
    }, 0);

    // header 在各區變色
    const sections = getSections();
    sections.forEach((section) => {
        if (section.ref.value) {
            const tl = headerChangeColor(
                section.ref.value,
                section.ref.value,
                () => {
                    setHeaderMode(section.mode());
                    event("user_engagement", {
                        event_category: "home",
                        event_label: section.label,
                    });
                },
                () => {
                    setHeaderMode(section.mode());
                    event("user_engagement", {
                        event_category: "home",
                        event_label: section.label,
                    });
                },
            );
            tls.push(tl);
        }
    });
};

// resize 事件
// 只在 breakpoint 真正改變時才 kill + rebuild，同 breakpoint 交由
// ScrollTrigger autoRefreshEvents="resize" 自動 refresh 即可
let currentBreakpoint = import.meta.client ? screenSize.value : "lg";
let resizeDebounceTimer = null;

watch(width, () => {
    if (!pageMounted.value) return;
    if (!import.meta.client) return;

    // 同 breakpoint → 不做 kill/rebuild，autoRefresh 已處理
    if (screenSize.value === currentBreakpoint) return;

    // 跨 breakpoint → kill + rebuild
    currentBreakpoint = screenSize.value;
    clearTimeout(resizeDebounceTimer);
    resizeDebounceTimer = setTimeout(() => {
        // 銷毀所有 GSAP 動畫
        tls.forEach((tl) => {
            if (tl) tl.kill();
        });
        tls = [];
        killScrollTrigger();

        // 重設樣式，避免 resize 後錯位
        if (focusSliderRef.value) {
            gsap.set(focusSliderRef.value, { clearProps: "all" });
            arrowHeight.value =
                focusSliderRef.value.getBoundingClientRect().height;
            cardWidth.value =
                focusSwiper.value.slides[0].getBoundingClientRect().width;
        }
        if (focusTextSwiper.value) {
            focusTextSwiper.value.update();
        }
        if (mockCarouselBlockRef.value) {
            gsap.set(mockCarouselBlockRef.value, { clearProps: "all" });
        }

        // 從桌面版縮小到平板/手機版時，重設相關狀態
        if (!isDesktop.value) {
            hasInitMockSwiper.value = false;
            accumulatedOffsetX.value = 0;
            updateTimes.value = 0;
        }

        // 重新啟動滾動動畫
        nextTick(() => {
            scrollAnimation();
        });
    }, 300);
});

// header 在各區變色設定
const getSections = () => [
    {
        ref: heroRef,
        mode: () => (mode.value === "egg" ? "dark" : "light"),
        label: "hero",
    },
    { ref: focusNewsRef, mode: () => "light", label: "焦點新聞" },
    { ref: aboutRef, mode: () => "silver", label: "關於橘子" },
    { ref: newsRef, mode: () => "dark", label: "閃光時刻" },
    { ref: gplayRef, mode: () => "light", label: "橘刻播放" },
    { ref: experienceRef, mode: () => "orange", label: "立即體驗" },
    { ref: esgRef, mode: () => "light", label: "ESG" },
];

onBeforeMount(() => {
    setMode("normal");
});

onMounted(() => {

    mockSlides.value = [];
    focusTitleRefs.value = [];
    experienceSlidesRefs.value = [];

    // 子品牌隨機排序
    initShuffledBrands();

    // 模型建立後，3D 場景開始渲染，並套用滾動動畫
    watchEffect(() => {
        if (globeRef.value && isGlobeReady.value) {
            globeRef.value.startAnimation();
            onGlobeReady();
        }
    });
});

onBeforeUnmount(() => {
    // 停止 newsGamaGlobe 跟隨滑鼠
    if (animationFrameId) {
        animation.stop();
    }
    if (import.meta.client) {
        clearTimeout(resizeDebounceTimer);
    }

    // 銷毀所有 Swiper 實例
    swipers.forEach((swiper) => {
        if (swiper && !swiper.destroyed) {
            swiper.destroy(true, true);
        }
        swiper = null;
    });
    swipers = [];

    // 銷毀所有 GSAP 動畫
    tls.forEach((tl) => {
        tl.kill();
        tl = null;
    });
    tls = [];

    // 銷毀所有 ScrollTrigger 動畫
    killScrollTrigger();

    // 清理引用
    mockSlides.value = [];
    focusTitleRefs.value = [];
    experienceSlidesRefs.value = [];
    mode.value = "normal";
});

await setMeta(
    contentData.value,
    imagesData.value.find(
        (item) => item.id === contentData.value?.featured_media,
    ),
);
setFocusNewsSchemaOrgVideo(focusNewsData.value);
</script>

<template>
    <div id="home" class="bg-base-bgGrey">
        <!-- Hero -->
        <div
            id="hero"
            ref="heroRef"
            class="slt-transition-color top-0 w-full bg-base-black"
            :class="{
                'relative text-base-black': mode === 'normal',
                'sticky text-primary-orange': mode === 'egg',
            }"
        >
            <div
                class="slt-transition-clip-path absolute left-0 top-0 h-full w-full bg-base-bgGrey will-change-[clip-path]"
                :class="{
                    '[clip-path:circle(Max(100vw,var(--window-height))_at_50%_80%)]':
                        mode === 'normal',
                    '[clip-path:circle(0px_at_50%_80%)] [transform:translateZ(0)]':
                        mode === 'egg',
                }"
            ></div>
            <div
                class="grid-container 3xl:bg-[length:169px_169px]bg-[67px_7px] absolute left-0 top-0 h-full w-full bg-[linear-gradient(to_right,rgba(53,53,53,0.5)_1px,transparent_1px),linear-gradient(to_bottom,rgba(53,53,53,0.5)_1px,transparent_1px)] bg-[length:18.83vw_18.83vw] xs:bg-[length:74px_74px] sm:bg-[length:102px_102px] sm:bg-[25px_10px] lg:bg-[length:8.8vw_8.8vw] lg:bg-[23px_17px]"
                :class="{
                    'opacity-0': !showEggTitle,
                    'opacity-100': showEggTitle,
                }"
            ></div>
            <div class="relative min-h-[var(--window-height,100vh)] w-full">
                <div
                    class="pointer-events-none pt-[--header-height] sm:pt-[calc(var(--header-height)+5.86vw)] md:pt-[calc(var(--header-height)+1.32vw)] 3xl:pt-[--header-height] min-h-[67vw] sm:min-h-[48vw] md:min-h-[40vw]"
                >
                    <h1
                        v-show="!showEggTitle"
                        class="slt-display-1 flex w-full select-none flex-wrap overflow-hidden md:justify-center"
                    >
                        <span
                            class="inline-block w-full overflow-hidden pl-[3.05vw] xs:pl-[12px] sm:pl-[5.21vw] md:w-[auto] md:pl-0"
                        >
                            <span
                                v-for="(
                                    text, index
                                ) in content?.hero?.title?.[0]?.split('')"
                                :key="text + index"
                                :class="{ active: pageMounted }"
                                class="slt-text-animation title inline-block whitespace-pre"
                                :style="{
                                    transitionDelay: `${delayList[0][index] * animationDelay + animationStart}s`,
                                }"
                                >{{ text }}</span
                            >
                        </span>
                        <span
                            class="-mt-[3%] inline-block w-full overflow-hidden pr-[3.05vw] text-right xs:pr-[12px] sm:-mt-[1%] sm:w-[auto] sm:pl-[5.21vw] sm:pr-0 sm:text-left md:mt-0 md:pl-0"
                        >
                            <span
                                v-for="(
                                    text, index
                                ) in content?.hero?.title?.[1]?.split('')"
                                :key="text + index"
                                :class="{ active: pageMounted }"
                                class="slt-text-animation title inline-block whitespace-pre"
                                :style="{
                                    transitionDelay: `${delayList[1][index] * animationDelay + animationStart}s`,
                                }"
                                >{{ text }}</span
                            >
                        </span>
                        <span
                            class="-mt-[3%] inline-block w-full overflow-hidden pl-[4.07vw] xs:pl-[16px] sm:-mt-[1%] sm:w-[auto] sm:pl-0 md:mt-0"
                        >
                            <span
                                v-for="(
                                    text, index
                                ) in content?.hero?.title?.[2]?.split('')"
                                :key="text + index"
                                :class="{ active: pageMounted }"
                                class="slt-text-animation title inline-block whitespace-pre"
                                :style="{
                                    transitionDelay: `${delayList[2][index] * animationDelay + animationStart}s`,
                                }"
                                >{{ text }}</span
                            >
                        </span>
                    </h1>
                    <h2
                        v-show="showEggTitle"
                        class="slt-display-2 mt-[4.07vw] px-[4.04vw] text-[#2d2d2d] xs:mt-[16px] xs:px-[16px] sm:mt-0 sm:px-[5.21vw] md:flex md:justify-center lg:mt-[1.32vw] lg:px-[2.18vw] xl:px-[33px] 3xl:mt-[25px]"
                    >
                        <span>
                            <span
                                v-for="(
                                    text, index
                                ) in content?.hero?.easterEggTitle?.[0]?.split(
                                    '',
                                )"
                                :key="text + index"
                                class="whitespace-pre"
                                >{{ text }}</span
                            >
                        </span>
                        <br class="md:hidden" />
                        <span>
                            <span
                                v-for="(
                                    text, index
                                ) in content?.hero?.easterEggTitle?.[1]?.split(
                                    '',
                                )"
                                :key="text + index"
                                class="whitespace-pre"
                                >{{ text }}</span
                            >
                        </span>
                    </h2>
                </div>
                <div
                    ref="globeContainerRef"
                    class="fixed left-0 top-0 w-screen overflow-hidden transition-transform duration-700 ease-in-out-quint"
                >
                    <ClientOnly>
                        <LazyGamaGlobe2D
                            ref="globeRef"
                            @ready="isGlobeReady = true"
                            :brandsData="filteredBrandsData"
                        />
                    </ClientOnly>
                </div>
                <div
                    class="pointer-events-none absolute bottom-[16.28vw] left-0 w-full xs:bottom-[64px] sm:bottom-[20%]"
                >
                    <div
                        class="flex w-full items-end justify-between px-[4.2vw] sm:px-[5.21vw] lg:px-[2.65vw] 3xl:px-[50.8px]"
                    >
                        <h3 v-show="!showEggTitle" class="slt-h3-tdd">
                            <span
                                v-for="item in content?.hero?.subtitle"
                                :key="item"
                                class="block overflow-hidden"
                            >
                                <span
                                    :class="{ active: pageMounted }"
                                    class="slt-text-animation subtitle inline-block"
                                    >{{ item }}</span
                                >
                            </span>
                        </h3>
                        <p
                            v-show="showEggTitle"
                            @click="globeRef.setNormal"
                            class="slt-h4-t pointer-events-auto flex cursor-pointer items-center justify-center gap-[2.03vw] hover:scale-[1.03] xs:gap-2"
                            v-sendGAsignal="{
                                event_category: 'home',
                                event_label: '關閉彩蛋',
                            }"
                        >
                            <span
                                class="h-[6.1vw] w-[6.1vw] xs:h-[24px] xs:w-[24px] lg:h-[1.59vw] lg:w-[1.59vw] 3xl:h-[30.5px] 3xl:w-[30.5px]"
                                ><SvgArrowBackAngle24
                                    :color="'#FF6400'"
                                    class="h-full w-full"
                            /></span>
                            BACK
                        </p>
                        <p class="slt-h6 slt-vertical-text overflow-hidden">
                            <span
                                :class="{ active: pageMounted }"
                                class="slt-text-animation subtitle flex flex-row items-center justify-center gap-[2.03vw] xs:gap-[8px]"
                            >
                                <span>SCROLL</span>
                                <span class="inline-block overflow-hidden">
                                    <span
                                        :class="{ active: pageMounted }"
                                        class="slt-text-animation arrow inline-flex h-[18px] items-center justify-end gap-[4px] overflow-hidden"
                                    >
                                        <SvgArrowDown16
                                            class="shrink-0"
                                            :isDark="mode === 'egg'"
                                            :class="{
                                                'slt-text-loopAnimation':
                                                    pageMounted,
                                            }"
                                        />
                                        <SvgArrowDown16
                                            class="shrink-0"
                                            :isDark="mode === 'egg'"
                                            :class="{
                                                'slt-text-loopAnimation':
                                                    pageMounted,
                                            }"
                                        />
                                    </span>
                                </span>
                            </span>
                        </p>
                    </div>
                </div>
            </div>
        </div>
        <!-- Focus -->
        <div
            v-if="focusNewsData?.length > 0"
            id="focus"
            ref="focusNewsRef"
            class="sticky top-0 h-[calc(var(--window-height)*2)] w-full"
            :class="{
                'relative bg-base-bgGrey': mode === 'egg' || mode === 'earth',
                'lg:h-[calc(var(--window-height)*2.5)]':
                    focusNewsData.length === 1,
                'lg:h-[calc(var(--window-height)*3)]':
                    focusNewsData.length === 2,
                'lg:h-[calc(var(--window-height)*3.5)]':
                    focusNewsData.length === 3,
                'lg:h-[calc(var(--window-height)*4)]':
                    focusNewsData.length === 4,
                'lg:h-[calc(var(--window-height)*4.5)]':
                    focusNewsData.length === 5,
            }"
        >
            <div
                class="sticky top-[calc(var(--header-height)+2%)] w-full overflow-x-clip sm:top-[calc(var(--header-height)+0.0439*var(--window-height))] lg:top-0 lg:flex lg:h-[--window-height] lg:items-center lg:justify-center"
            >
                <div
                    id="focus-block"
                    class="w-full lg:relative lg:ml-[4.76vw] lg:flex lg:items-center lg:justify-end lg:gap-[2.91vw]"
                >
                    <div
                        ref="focusSliderRef"
                        class="relative z-10 w-full lg:absolute lg:left-0 lg:top-0 lg:w-[119vw] lg:translate-x-[-19vw]"
                    >
                        <Swiper
                            v-if="width < 1270"
                            :slidesPerView="
                                width < 640
                                    ? Math.max(width * 0.00356, 1.4)
                                    : Math.max(
                                          Math.max(width * 0.00218, 1.4),
                                          Math.min(-0.01 * height + 11, 4),
                                      )
                            "
                            :loop="true"
                            :spaceBetween="20"
                            :centeredSlides="true"
                            :breakpoints="{
                                393: {
                                    spaceBetween: 24,
                                },
                                640: {
                                    spaceBetween: 32,
                                },
                            }"
                            @swiper="focusSwiperInit"
                            @slideChange="focusSwiperSlideChange"
                        >
                            <SwiperSlide
                                v-for="(item, index) in [
                                    ...focusNewsData,
                                    ...focusNewsData,
                                ]"
                                :key="item.date + index"
                                :data-no-parse="index >= focusNewsData.length"
                                class="focus-slide group relative max-h-[calc(var(--window-height)*0.645)] w-[69.72vw] overflow-hidden rounded-[4.07vw] xs:w-[274px] xs:rounded-[16px] sm:w-[55.73vw] sm:rounded-[2.08vw]"
                            >
                                <NuxtImg
                                    width="418"
                                    height="550"
                                    fetchpriority="high"
                                    data-no-hydration="src"
                                    format="webp"
                                    :src="item?.featured_media?.url"
                                    :alt="item?.featured_media?.alt"
                                    sizes="274px sm:56vw 3xl:301px"
                                    class="aspect-[418/550] w-full object-cover object-center group-hover:scale-[1.05]"
                                    :loading="
                                        index === 0 ||
                                        index === 1 ||
                                        index === focusNewsData.length * 2 - 1
                                            ? 'eager'
                                            : 'lazy'
                                    "
                                />
                                <video
                                    v-if="item?.meta_box?.video?.length > 0"
                                    muted
                                    width="418"
                                    height="550"
                                    playsinline
                                    loading="lazy"
                                    data-no-hydration="src"
                                    :data-src="item?.meta_box?.video"
                                    class="absolute inset-0 aspect-[418/550] h-full w-full object-cover object-center opacity-0 transition-opacity duration-300"
                                />
                                <a
                                    v-if="item?.meta_box?.href"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    class="absolute inset-0"
                                    :href="item?.meta_box?.href"
                                    :aria-label="
                                        $t('ariaLabel.index.focusNews')
                                    "
                                    v-sendGAsignal="{
                                        event_category: 'home.focusNews',
                                        event_label:
                                            item?.meta_box?.article?.headline,
                                    }"
                                ></a>
                            </SwiperSlide>
                        </Swiper>
                        <Swiper
                            v-if="width >= 1270"
                            :slidesPerView="
                                Math.max(
                                    Math.min(width * 0.0026, 4.5),
                                    Math.min(0.0015 * height + 5.22, 4.5),
                                )
                            "
                            :loop="true"
                            :longSwipesMs="0"
                            :spaceBetween="20"
                            :watchSlidesProgress="true"
                            @swiper="focusSwiperInit"
                            @setTransition="handleSetTransition"
                            @slideChange="focusSwiperSlideChange"
                            class="swiper-3d swiper-creative focus-swiper [perspective:1200px]"
                        >
                            <SwiperSlide
                                v-for="(item, index) in processedFocusNewsData"
                                :key="item.date + index"
                                class="focus-slide group relative overflow-hidden lg:rounded-[1.59vw] 3xl:rounded-[30.5px]"
                                :class="{
                                    'opacity-0': index < focusSwiper?.realIndex,
                                }"
                                :data-no-parse="index >= focusNewsData.length"
                            >
                                <div class="slide-content relative">
                                    <NuxtImg
                                        width="418"
                                        height="550"
                                        fetchpriority="high"
                                        data-no-hydration="src"
                                        format="webp"
                                        :src="item?.featured_media?.url"
                                        :alt="item?.featured_media?.alt"
                                        class="aspect-[418/550] w-full object-cover object-center group-hover:scale-[1.05]"
                                    />
                                    <video
                                        v-if="item?.meta_box?.video?.length > 0"
                                        muted
                                        width="418"
                                        height="550"
                                        playsinline
                                        loading="lazy"
                                        data-no-hydration="src"
                                        :data-src="item?.meta_box?.video"
                                        class="absolute inset-0 aspect-[418/550] h-full w-full object-cover object-center opacity-0 transition-opacity duration-300"
                                    />
                                    <a
                                        v-if="item?.meta_box?.href"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        class="absolute inset-0"
                                        :href="item?.meta_box?.href"
                                        :aria-label="
                                            $t('ariaLabel.index.focusNews')
                                        "
                                        v-sendGAsignal="{
                                            event_category: 'home.focusNews',
                                            event_label:
                                                item?.meta_box?.article
                                                    ?.headline,
                                        }"
                                    ></a>
                                </div>
                            </SwiperSlide>
                        </Swiper>
                    </div>
                    <div
                        class="relative mt-[6.1vw] flex items-end justify-between p-[4.07vw] pt-0 xs:mt-[24px] xs:p-[16px] xs:pt-0 sm:mt-[calc(var(--window-height)*0.03)] sm:px-[5.21vw] sm:pb-[calc(var(--window-height)*0.047)] lg:m-0 lg:flex-1 lg:p-0"
                    >
                        <Swiper
                            :loop="true"
                            :slidesPerView="1"
                            :direction="'vertical'"
                            :centeredSlides="false"
                            @swiper="focusTextSwiperInit"
                            class="h-[35.62vw] w-full xs:h-[140px] sm:h-[25vw] sm:flex-1"
                        >
                            <SwiperSlide
                                v-for="(item, index) in width >= 1270
                                    ? processedFocusNewsData
                                    : [...focusNewsData, ...focusNewsData]"
                                :key="item.date + index"
                                :data-no-parse="index >= focusNewsData.length"
                                class="!flex flex-col justify-end sm:justify-start lg:!flex lg:!flex-col lg:!justify-center"
                            >
                                <div
                                    class="flex flex-col items-start justify-center gap-[2.03vw] xs:gap-[8px] sm:gap-[12px] lg:gap-[16px] lg:pl-[2.12vw] 3xl:gap-[20.3px] 3xl:pl-[40.6px]"
                                >
                                    <BaseFormatText2
                                        :ref="
                                            (el) => {
                                                if (
                                                    el &&
                                                    !focusTitleRefs.includes(el)
                                                ) {
                                                    focusTitleRefs.push(el);
                                                }
                                            }
                                        "
                                        type="h2"
                                        :isAutoRevert="false"
                                        :text="item?.meta_box?.title"
                                        :customClass="'leading-[1.5]'"
                                        class="slt-h1 w-full whitespace-pre leading-[normal] lg:whitespace-normal break-words"
                                    />
                                    <p
                                        class="slt-text-show slt-body-1 slt-line-clamp-1 w-full text-base-black-60"
                                        :class="{
                                            'opacity-100': isFocusGamaGlobeShow,
                                            'opacity-0': !isFocusGamaGlobeShow,
                                        }"
                                    >
                                        <span
                                            v-for="text in item?.meta_box
                                                ?.description"
                                            :key="text"
                                            class="block"
                                            v-html="htmlPurify(text)"
                                        ></span>
                                    </p>
                                </div>
                            </SwiperSlide>
                        </Swiper>
                    </div>
                    <div
                        v-if="width >= 1270"
                        id="carousel-block"
                        class="focus-in-scroll hidden items-center justify-end lg:flex"
                        :style="{
                            height: arrowHeight + 'px',
                            width:
                                width >= 1270
                                    ? `${(width + cardWidth) / 2}px`
                                    : 'auto',
                        }"
                    >
                        <div
                            id="mock-carousel-block"
                            ref="mockCarouselBlockRef"
                            class="pointer-events-none relative z-10 flex items-center opacity-0"
                        >
                            <div
                                v-for="(item, index) in leftFocusNewsData"
                                :data-no-parse="true"
                                :key="item.date + index"
                                class="focus-slide carousel-block-slide group relative overflow-hidden lg:rounded-[1.59vw] 3xl:rounded-[30.5px]"
                                :ref="
                                    (el) => {
                                        if (el && !mockSlides.includes(el)) {
                                            mockSlides.push(el);
                                        }
                                    }
                                "
                            >
                                <div class="slide-content relative">
                                    <NuxtImg
                                        width="418"
                                        height="550"
                                        loading="lazy"
                                        data-no-hydration="src"
                                        format="webp"
                                        :src="item?.featured_media?.url"
                                        :alt="item?.featured_media?.alt"
                                        class="aspect-[418/550] w-full object-cover object-center group-hover:scale-[1.05]"
                                    />
                                    <video
                                        v-if="item?.meta_box?.video?.length > 0"
                                        muted
                                        playsinline
                                        width="418"
                                        height="550"
                                        loading="lazy"
                                        data-no-hydration="src"
                                        :data-src="item?.meta_box?.video"
                                        class="absolute inset-0 aspect-[418/550] h-full w-full object-cover object-center opacity-0 transition-opacity duration-300"
                                    />
                                    <a
                                        v-if="item?.meta_box?.href"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        class="absolute inset-0"
                                        :href="item?.meta_box?.href"
                                        :aria-label="
                                            $t('ariaLabel.index.focusNews')
                                        "
                                        v-sendGAsignal="{
                                            event_category: 'home.focusNews',
                                            event_label:
                                                item?.meta_box?.article
                                                    ?.headline,
                                        }"
                                    ></a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    v-if="width >= 1270"
                    class="slt-transition-globe absolute bottom-[4.41%] left-[2.12vw] h-[7.94vw] max-h-[120px] w-[7.94vw] max-w-[120px] 3xl:left-[40.6px]"
                    :class="{
                        'translate-y-0': isFocusGamaGlobeShow,
                        'translate-y-[calc(var(--window-height)*0.0441+100%)]':
                            !isFocusGamaGlobeShow,
                    }"
                >
                    <GamaGlobeLottie ref="focusGamaGlobe" />
                </div>
            </div>
        </div>
        <!-- About -->
        <div
            v-if="brandsData?.length > 0"
            id="about"
            ref="aboutRef"
            class="sticky top-0 h-[calc(var(--window-height)*3.5)] w-full bg-base-silver"
        >
            <div
                class="sticky top-0 flex min-h-[--window-height] items-center justify-center overflow-x-clip"
            >
                <div
                    class="flex flex-col gap-[10.18vw] overflow-y-hidden overflow-x-clip px-[4.07vw] py-[20.36vw] xs:gap-[40px] xs:px-[16px] xs:py-[80px] sm:gap-[56px] sm:px-[5.21vw] sm:py-[calc(var(--window-height)*0.078)] lg:gap-24 lg:px-[6.35vw] lg:py-[calc(var(--window-height)*0.1324)] 3xl:px-[122px]"
                >
                    <div
                        class="slt-container flex flex-col items-start justify-center sm:items-center sm:text-center"
                    >
                        <div
                            class="w-[20.36vw] translate-y-[calc(-20.36vw-100%)] xs:w-[80px] xs:translate-y-[calc(-80px-100%)] sm:inline-block sm:w-[10.42vw] sm:translate-y-[calc(var(--window-height)*-0.078-100%)] lg:w-[6.88vw] lg:translate-y-[calc(var(--window-height)*-0.1324-100%)] 3xl:w-[132px]"
                            ref="aboutGamaGlobeRef"
                        >
                            <GamaGlobeLottie
                                ref="aboutGamaGlobe"
                                class="h-full w-full"
                            />
                        </div>
                        <div
                            class="mt-[6.1vw] w-full xs:mt-[24px] lg:mt-[3.17vw] lg:w-[90%] 3xl:mt-[61px]"
                        >
                            <h3
                                class="slt-h1 leading-[1.4] tracking-[-0.78px] sm:text-[4.43vw] sm:tracking-[-1.02px] lg:text-[2.91vw] lg:tracking-[-1.32px] 3xl:text-[55.9px]"
                                ref="aboutTitleRef"
                            >
                                <span
                                    v-for="text in content?.about?.title"
                                    :key="text"
                                    v-html="htmlPurify(text)"
                                    class="slt-text-fill-animation sm:block"
                                ></span>
                            </h3>
                        </div>
                    </div>
                    <div
                        ref="aboutBrandRef"
                        class="grid w-full grid-cols-2 gap-[4.07vw] px-[2.03vw] xs:gap-[16px] xs:px-[8px] sm:grid-cols-4 sm:gap-[2.08vw] sm:px-0 lg:flex lg:flex-nowrap lg:gap-[1.59vw] 3xl:gap-[30.5px]"
                    >
                        <div
                            v-for="item in shuffledBrands"
                            :key="item?.meta_box?.brand?.name"
                            class="relative flex aspect-[164/85] items-center justify-center overflow-hidden rounded-[4.07vw] bg-base-white xs:rounded-[16px] sm:aspect-[160/84] sm:rounded-[2.08vw] lg:aspect-[312/160] lg:w-[20.63vw] lg:shrink-0 lg:rounded-[1.59vw] 3xl:w-[396px] 3xl:rounded-[30.5px]"
                        >
                            <NuxtImg
                                width="330"
                                height="173"
                                loading="lazy"
                                format="webp"
                                sizes="291px sm:26vw 3xl:486px"
                                class="w-full lg:w-[76%]"
                                :src="item?.featured_media?.url"
                                :alt="item?.featured_media?.alt"
                            />
                            <a
                                v-if="item?.meta_box?.brand?.href?.length > 0"
                                target="_blank"
                                rel="noopener noreferrer"
                                class="absolute inset-0"
                                :href="item?.meta_box?.brand?.href"
                                :aria-label="$t('ariaLabel.index.brand')"
                                v-sendGAsignal="{
                                    event_category: 'home.brands',
                                    event_label: item?.meta_box?.brand?.name,
                                }"
                            ></a>
                        </div>
                    </div>
                    <div
                        class="w-full sm:w-[37.6vw] sm:self-center lg:w-[auto]"
                    >
                        <BaseIconButton
                            variant="white"
                            :ariaLabel="$t('ariaLabel.index.about')"
                            :to="$localePath(content?.about?.button?.link)"
                            v-sendGAsignal="{
                                event_category:
                                    content?.about?.button?.category,
                                event_label: content?.about?.button?.label,
                            }"
                        >
                            {{ content?.about?.button?.text }}
                        </BaseIconButton>
                    </div>
                </div>
            </div>
        </div>
        <!-- News -->
        <div
            v-if="newsData?.length > 0"
            id="news"
            ref="newsRef"
            @mousemove="handleMouseMove"
            @mouseenter="handleMouseMove"
            class="sticky top-0 w-full bg-base-black"
        >
            <div
                v-if="width >= 1270"
                class="absolute left-0 top-0 h-full w-full overflow-hidden"
            >
                <div
                    class="absolute left-0 top-0 h-[33vw] max-h-[500px] w-[33vw] max-w-[500px] -translate-y-full translate-x-[calc(50vw-50%)]"
                >
                    <GamaGlobeLottie ref="newsGamaGlobe" />
                </div>
            </div>
            <div
                class="slt-container relative flex flex-col items-center justify-center gap-[12.21vw] px-[4.07vw] py-[20.36vw] xs:gap-[48px] xs:px-[16px] xs:py-[80px] sm:gap-[80px] sm:px-[5.21vw] sm:py-[80px] lg:gap-[5.29vw] lg:px-[6.35vw] lg:pb-[calc(var(--window-height)*0.1634)] lg:pt-[calc(var(--window-height)*0.3974)] 3xl:gap-[101.6px] 3xl:px-[122px]"
            >
                <div
                    class="news-title slt-h2 text-base-white sm:mb-[8px] lg:mb-[1.98vw] 3xl:mb-[38.1px]"
                >
                    <BaseFormatText2
                        type="h2"
                        ref="newsTitleRef"
                        class="whitespace-pre"
                        :isAutoRevert="false"
                        :text="content?.news?.title"
                    />
                </div>
                <div
                    class="news-posts relative flex w-full flex-col gap-[4.07vw] xs:gap-[16px] sm:flex-row sm:flex-wrap sm:gap-[7.29vw] sm:gap-y-0 lg:gap-0"
                >
                    <div
                        v-for="(item, index) in newsData"
                        :key="item"
                        class="pointer-events-none sm:w-full"
                        :class="{
                            'sm:flex sm:justify-end': index % 2 !== 0,
                            'sm:mt-[-180px]': index >= 1,
                        }"
                    >
                        <NuxtLink
                            class="news-card pointer-events-auto flex w-full flex-col gap-[4.07vw] rounded-[4.07vw] p-[4.07vw] pb-[6.1vw] xs:gap-[16px] xs:rounded-[16px] xs:p-[16px] xs:pb-[24px] sm:w-[41.14vw] sm:gap-[2.08vw] sm:rounded-[2.08vw] sm:p-[2.08vw] sm:pb-[3.125vw] lg:w-[39.29vw] lg:gap-[1.59vw] lg:rounded-[1.59vw] lg:p-[1.59vw] 3xl:w-[754.3px] 3xl:gap-[30.5px] 3xl:rounded-[30.5px] 3xl:p-[30.5px]"
                            :to="$localePath(`/news/${item?.id}`)"
                            :style="{ backgroundColor: colors[index] }"
                            :aria-label="$t('ariaLabel.index.newsItem')"
                            v-sendGAsignal="{
                                event_category: 'home.news',
                                event_label: `${item?.id}-${item?.meta_box?.article.headline}`,
                            }"
                        >
                            <div
                                class="w-full overflow-hidden rounded-[2.03vw] xs:rounded-[8px] lg:rounded-[0.79vw] 3xl:rounded-[15.2px]"
                            >
                                <NuxtImg
                                    loading="lazy"
                                    sizes="90vw sm:60vw"
                                    format="webp"
                                    :src="item?.featured_media?.url"
                                    :alt="item?.featured_media?.alt"
                                    class="w-full object-cover object-center"
                                    :class="{
                                        'aspect-[361/496]':
                                            width < 640 &&
                                            item?.meta_box?.customImage
                                                ?.mobileImage,
                                        'aspect-[948/496]':
                                            width >= 640 ||
                                            !item?.meta_box?.customImage
                                                ?.mobileImage,
                                    }"
                                />
                            </div>
                            <div
                                class="flex flex-col gap-[2.03vw] px-[2.03vw] xs:gap-[8px] xs:px-[8px] lg:gap-[1.06vw] 3xl:gap-[20.3px]"
                            >
                                <p class="slt-h5">
                                    {{ item?.date?.split(" ")[0] }}
                                </p>
                                <h3 class="slt-h3-ttd slt-line-clamp-3">
                                    <span
                                        v-for="text in item?.meta_box
                                            ?.customTitle"
                                        :key="text"
                                        class="block"
                                        v-html="htmlPurify(text)"
                                    ></span>
                                </h3>
                                <p
                                    class="slt-line-clamp-2 hidden text-base-black-60 lg:mt-[8px] lg:block"
                                >
                                    <span
                                        v-for="text in item?.meta_box
                                            ?.customDescription"
                                        :key="text"
                                        class="block"
                                        v-html="htmlPurify(text)"
                                    ></span>
                                </p>
                            </div>
                            <div
                                class="relative mr-[2.03vw] h-[6.1vw] w-[6.1vw] self-end overflow-hidden xs:mr-[8px] xs:h-[24px] xs:w-[24px] lg:h-[1.59vw] lg:w-[1.59vw] 3xl:h-[30.5px] 3xl:w-[30.5px]"
                            >
                                <SvgArrowForward24
                                    class="btn__arrow btn__arrow--main h-full w-full"
                                />
                                <SvgArrowForward24
                                    class="btn__arrow btn__arrow--hover h-full w-full"
                                />
                            </div>
                        </NuxtLink>
                    </div>
                </div>
                <div
                    class="news-button relative w-full sm:w-[37.6vw] sm:self-center lg:w-[auto]"
                >
                    <BaseIconButton
                        variant="secondary"
                        :ariaLabel="$t('ariaLabel.index.news')"
                        :to="$localePath(content?.news?.button?.link)"
                        v-sendGAsignal="{
                            event_category: content?.news?.button?.category,
                            event_label: content?.news?.button?.label,
                        }"
                    >
                        {{ content?.news?.button?.text }}
                    </BaseIconButton>
                </div>
            </div>
        </div>
        <!-- Shorts -->
        <div
            v-if="gplayData?.length > 0"
            id="shorts"
            ref="gplayRef"
            class="sticky top-0 w-full bg-base-bgGrey sm:h-[calc(var(--window-height)*3)]"
        >
            <div
                v-if="width >= 1270"
                class="absolute bottom-[calc(var(--window-height)*2.0441)] left-[2.12vw] h-[7.94vw] max-h-[120px] w-[7.94vw] max-w-[120px] translate-y-[calc(var(--window-height)*0.0441+100%)] 3xl:left-[40.6px]"
                ref="gplayGamaGlobeRef"
            >
                <GamaGlobeLottie ref="gplayGamaGlobe" />
            </div>
            <div
                class="slt-container top-0 flex w-full flex-col items-center justify-center gap-[8.14vw] overflow-hidden px-[4.07vw] py-[20.36vw] xs:gap-[32px] xs:px-[16px] xs:py-[80px] sm:sticky sm:px-[5.21vw] sm:py-[calc(var(--window-height)*0.078)] lg:h-[--window-height] lg:gap-[2.65vw] lg:px-[2.65vw] lg:py-0 3xl:gap-[50.8px] 3xl:px-[50.8px]"
            >
                <div class="slt-h2">
                    <BaseFormatText2
                        type="h2"
                        ref="gplayTitleRef"
                        class="whitespace-pre"
                        :isAutoRevert="false"
                        :text="content?.gplay?.title"
                    />
                </div>
                <div
                    class="relative flex flex-col gap-[8.14vw] xs:gap-[32px] lg:w-full lg:flex-row lg:items-center lg:justify-center lg:gap-0"
                >
                    <!-- Mobile / Tablet Index -->
                    <div
                        v-if="width < 1270"
                        class="slt-display-7 flex items-center justify-center text-center lg:hidden"
                    >
                        <span
                            class="slt-display-4-t inline-block w-[10.18vw] xs:w-[40px]"
                            >{{
                                (gplaySwiper?.realIndex %
                                    processedGplayData.length) +
                                1
                            }}</span
                        >/{{ processedGplayData.length }}
                    </div>
                    <div
                        class="flex w-screen items-center justify-center py-4 sm:w-full sm:p-4 lg:w-[auto] lg:p-0"
                    >
                        <!-- Mobile Swiper -->
                        <Swiper
                            v-if="width < 640"
                            :loop="true"
                            :spaceBetween="16"
                            :slidesPerView="1.5"
                            :centeredSlides="true"
                            @swiper="gplaySwiperInit"
                            @slideChange="gplaySwiperSlideChange"
                        >
                            <SwiperSlide
                                v-for="item in [
                                    ...processedGplayData,
                                    ...processedGplayData,
                                    ...processedGplayData,
                                ]"
                                :key="item"
                                class="relative overflow-hidden rounded-[4.07vw] border-[0.8px] border-base-silver xs:rounded-[16px]"
                            >
                                <NuxtLink
                                    class="relative block h-full w-full"
                                    :to="$localePath(`/gplay/${item?.id}`)"
                                    :aria-label="
                                        $t('ariaLabel.index.gplayItem')
                                    "
                                    v-sendGAsignal="{
                                        event_category: 'home.gplay',
                                        event_label: `${item?.id}-${item?.videoTitle}`,
                                    }"
                                >
                                    <NuxtImg
                                        width="310"
                                        height="408"
                                        loading="lazy"
                                        format="webp"
                                        sizes="294px sm:60vw"
                                        :src="item?.featured_media?.url"
                                        :alt="item?.featured_media?.alt"
                                        class="aspect-[310/408] w-full object-cover object-center"
                                    />
                                    <video
                                        loop
                                        muted
                                        width="310"
                                        height="408"
                                        playsinline
                                        loading="lazy"
                                        data-no-hydration="src"
                                        :data-src="item?.preview?.[0]"
                                        class="absolute inset-0 left-0 top-0 aspect-[310/408] h-full w-full object-cover object-center"
                                    />
                                </NuxtLink>
                            </SwiperSlide>
                        </Swiper>
                        <!-- Tablet / Desktop Swiper -->
                        <Swiper
                            v-if="width >= 640"
                            :loop="false"
                            :effect="'cards'"
                            :modules="[EffectCards]"
                            @swiper="gplaySwiperInit"
                            @slideChange="gplaySwiperSlideChange"
                            class="w-[65vw] xs:w-[242px] sm:w-[291px] lg:max-h-[50vh] lg:w-[25.4vw] lg:max-w-[calc(50vh*310/408)] 3xl:w-[486px]"
                            :cardsEffect="{
                                slideShadows: false,
                            }"
                        >
                            <SwiperSlide
                                v-for="item in processedGplayData"
                                :key="item"
                                class="relative aspect-[310/408] overflow-hidden border-[0.8px] border-base-silver xs:rounded-[16px] lg:rounded-[1.06vw] 3xl:rounded-[20.3px]"
                            >
                                <NuxtLink
                                    class="relative block h-full w-full"
                                    :to="$localePath(`/gplay/${item?.id}`)"
                                    :aria-label="
                                        $t('ariaLabel.index.gplayItem')
                                    "
                                    v-sendGAsignal="{
                                        event_category: 'home.gplay',
                                        event_label: `${item?.id}-${item?.videoTitle}`,
                                    }"
                                >
                                    <NuxtImg
                                        width="310"
                                        height="408"
                                        loading="lazy"
                                        format="webp"
                                        :src="item?.featured_media?.url"
                                        :alt="item?.featured_media?.alt"
                                        class="aspect-[310/408] w-full object-cover object-center"
                                    />
                                    <video
                                        loop
                                        muted
                                        playsinline
                                        width="310"
                                        height="408"
                                        sloading="lazy"
                                        data-no-hydration="src"
                                        :data-src="item?.preview?.[0]"
                                        class="absolute inset-0 aspect-[310/408] h-full w-full object-cover object-center"
                                    />
                                </NuxtLink>
                            </SwiperSlide>
                        </Swiper>
                    </div>
                    <div
                        class="flex w-full p-[4.07vw] xs:p-[16px] sm:p-0 lg:absolute lg:left-0 lg:top-[50%] lg:translate-y-[-50%] lg:flex-row lg:justify-between"
                    >
                        <div
                            v-if="width >= 1270"
                            class="lg:w-[30.82vw] lg:pr-[5.42vw] 3xl:w-[591.7px] 3xl:pr-[104.1px]"
                        >
                            <div
                                data-no-parse="true"
                                class="slt-display-7 hidden lg:flex lg:items-start"
                            >
                                <span
                                    class="slt-display-4-t inline-block w-[3.77vw] 3xl:w-[72.4px]"
                                    >{{ gplaySwiper?.realIndex + 1 }}</span
                                ><span
                                    class="lg:inline-block lg:translate-y-[1.06vw] 3xl:translate-y-[20.3px]"
                                    >/{{ processedGplayData?.length }}</span
                                >
                            </div>
                            <h3
                                class="slt-h3-ddd mt-[2.12vw] hidden lg:block 3xl:mt-[40.6px]"
                                data-no-parse="true"
                            >
                                <span
                                    v-for="(text, index) in processedGplayData[
                                        (gplaySwiper?.realIndex || 0) %
                                            processedGplayData?.length
                                    ]?.title"
                                    :key="text + index"
                                    class="block"
                                    v-html="htmlPurify(text)"
                                ></span>
                            </h3>
                        </div>
                        <div
                            class="flex flex-col gap-[6.1vw] text-center xs:gap-[24px] lg:w-[30.82vw] lg:justify-center lg:self-stretch lg:text-right 3xl:w-[591.7px]"
                        >
                            <!-- Mobile / Tablet Title -->
                            <h3
                                v-if="width < 1270"
                                class="slt-h3-ddd lg:hidden"
                            >
                                <span
                                    v-for="(text, index) in processedGplayData[
                                        (gplaySwiper?.realIndex || 0) %
                                            processedGplayData?.length
                                    ]?.title"
                                    :key="text + index"
                                    class="block"
                                    v-html="htmlPurify(text)"
                                ></span>
                            </h3>
                            <p class="slt-h4-t slt-line-clamp-5 text-base-grey">
                                {{
                                    processedGplayData[
                                        (gplaySwiper?.realIndex || 0) %
                                            processedGplayData?.length
                                    ]?.tags
                                        ?.map((tag) => `#${tag}`)
                                        .join(" ")
                                }}
                            </p>
                            <div
                                class="flex flex-row items-center justify-center gap-[4px] lg:justify-end"
                            >
                                <div
                                    v-for="item in processedGplayData[
                                        (gplaySwiper?.realIndex || 0) %
                                            processedGplayData?.length
                                    ]?.categories"
                                    :key="item"
                                    class="slt-body-3 flex h-[6.62vw] items-center justify-center rounded-[999px] bg-base-dimGrey px-[3.05vw] text-base-white xs:h-[26px] xs:px-[12px] sm:h-[28px] lg:h-[1.85vw] lg:px-[0.79vw] 3xl:h-[35.6px] 3xl:px-[15.2px]"
                                >
                                    <p>
                                        {{ item }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    class="w-full sm:w-[37.6vw] sm:self-center lg:mt-[1.32vw] lg:w-[auto] 3xl:mt-[25.4px]"
                >
                    <BaseIconButton
                        class="w-full"
                        variant="secondary"
                        :ariaLabel="$t('ariaLabel.index.gplay')"
                        :to="$localePath(content?.gplay?.button?.link)"
                        v-sendGAsignal="{
                            event_category: content?.gplay?.button?.category,
                            event_label: content?.gplay?.button?.label,
                        }"
                    >
                        {{ content?.gplay?.button?.text }}
                    </BaseIconButton>
                </div>
            </div>
        </div>
        <!-- Experience -->
        <div
            v-if="content?.experiences?.isActive"
            id="experiences"
            ref="experienceRef"
            class="sticky top-0 overflow-hidden bg-primary-orange sm:h-[calc(var(--window-height)*2)]"
        >
            <div
                v-if="width >= 1270"
                class="absolute bottom-[calc(var(--window-height)*1.0441)] left-[2.12vw] h-[7.94vw] max-h-[120px] w-[7.94vw] max-w-[120px] translate-y-[calc(var(--window-height)*0.0441+100%)] 3xl:left-[40.6px]"
                ref="experienceGamaGlobeRef"
            >
                <GamaGlobeLottie ref="experienceGamaGlobe" :isBlack="true" />
            </div>
            <div
                class="slt-container w-full py-[20.36vw] xs:py-[80px] sm:py-[calc(var(--window-height)*0.078)] lg:flex lg:h-[--window-height] lg:flex-col lg:justify-start lg:gap-[calc(var(--window-height)*0.0618)] lg:px-[2.65vw] lg:pb-0 lg:pt-[calc(var(--window-height)*0.1324)] 3xl:px-[50.8px]"
            >
                <div class="slt-h2 text-center lg:text-left">
                    <BaseFormatText2
                        type="h2"
                        :isAutoRevert="false"
                        class="whitespace-pre"
                        ref="experienceTitleRef"
                        :text="content.experiences.title"
                    />
                </div>
                <!-- Mobile & Tablet Swiper -->
                <Swiper
                    v-if="width < 1270"
                    :slidesPerView="
                        width < 640
                            ? Math.max(width * 0.0038, 1.5)
                            : Math.max(
                                  Math.max(width * 0.0032, 1.5),
                                  Math.min(-0.01 * height + 11.8, 4),
                              )
                    "
                    :initialSlide="1"
                    :spaceBetween="32"
                    :centeredSlides="true"
                    @swiper="experienceSwiperInit"
                    @slideChange="experienceSwiperSlideChange"
                    class="mt-[10.18vw] !pb-[4.07vw] xs:mt-[40px] xs:!pb-[16px] sm:mt-[56px] sm:pb-0"
                    :style="{ overflow: 'visible' }"
                >
                    <SwiperSlide
                        v-for="(item, index) in experienceData"
                        :key="item"
                        class="experience-slide"
                    >
                        <div
                            class="flex translate-y-full flex-col items-center justify-center gap-[4.07vw] xs:gap-[16px] sm:gap-[2.08vw]"
                        >
                            <div class="relative w-full shrink-0">
                                <div
                                    class="absolute left-[4%] top-[2%] h-[96%] w-[92%] overflow-hidden rounded-[6.1vw] xs:rounded-[24px]"
                                >
                                    <NuxtImg
                                        width="486"
                                        height="1051"
                                        loading="lazy"
                                        format="webp"
                                        sizes="301px sm:60vw md:80vw"
                                        class="w-full object-cover object-center"
                                        :src="
                                            imagesData.find(
                                                (image) =>
                                                    image.id ==
                                                    item?.image?.[0],
                                            )?.source_url
                                        "
                                        :alt="
                                            imagesData.find(
                                                (image) =>
                                                    image.id ==
                                                    item?.image?.[0],
                                            )?.alt_text
                                        "
                                    />
                                </div>
                                <a
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    :href="item.href"
                                    class="relative w-full cursor-pointer"
                                    :aria-label="
                                        $t('ariaLabel.index.experience')
                                    "
                                    v-sendGAsignal="{
                                        event_category: 'home.experience',
                                        event_label: item?.description,
                                    }"
                                >
                                    <img
                                        width="269"
                                        height="552"
                                        loading="lazy"
                                        class="w-full"
                                        alt="立即體驗-手機外框"
                                        src="~/assets/images/pages/index/experience-phone.png"
                                    />
                                </a>
                            </div>
                            <div
                                class="mx-auto hidden w-full items-center justify-center gap-[16px] sm:flex lg:hidden"
                                data-no-parse="true"
                            >
                                <div
                                    class="flex flex-1 items-center justify-center pl-[8px] sm:h-[48px]"
                                >
                                    <p
                                        class="slt-h4-t flex items-center justify-start"
                                    >
                                        <span
                                            class="block"
                                            :key="item.name"
                                            v-html="htmlPurify(item.name)"
                                        >
                                        </span>
                                    </p>
                                </div>
                                <BaseIconButton
                                    :href="item.href"
                                    :isExternal="true"
                                    variant="secondary"
                                    :ariaLabel="
                                        $t('ariaLabel.index.experience')
                                    "
                                    v-sendGAsignal="{
                                        event_category: 'home.experience',
                                        event_label: item?.description,
                                    }"
                                ></BaseIconButton>
                            </div>
                        </div>
                    </SwiperSlide>
                </Swiper>
                <div
                    v-if="width < 640"
                    class="mx-auto flex w-[63.61vw] items-center justify-center gap-[4.07vw] xs:w-[250px] xs:gap-[16px] sm:hidden"
                >
                    <div
                        class="h-[10.69vw] flex-1 overflow-hidden pl-[2.03vw] xs:h-[42px] xs:pl-[8px]"
                    >
                        <p
                            class="slt-h4 flex flex-col items-start gap-2 transition-transform duration-300 ease-in-out-cubic"
                            :class="{
                                'translate-y-[calc(-10.69vw-0.5rem)] xs:translate-y-[calc(-42px-0.5rem)]':
                                    experienceActiveSlide === 1,
                                'translate-y-[calc(-21.38vw-1rem)] xs:translate-y-[calc(-84px-1rem)]':
                                    experienceActiveSlide === 2,
                            }"
                        >
                            <span
                                v-for="item in experienceData"
                                :key="item.name"
                                v-html="htmlPurify(item.name)"
                                class="flex h-[10.69vw] items-center justify-center xs:h-[42px]"
                            >
                            </span>
                        </p>
                    </div>
                    <BaseIconButton
                        :isExternal="true"
                        variant="secondary"
                        :ariaLabel="$t('ariaLabel.index.experience')"
                        :to="experienceData?.[experienceActiveSlide]?.href"
                        v-sendGAsignal="{
                            event_category: 'home.experience',
                            event_label:
                                experienceData?.[experienceActiveSlide]
                                    ?.description,
                        }"
                    ></BaseIconButton>
                </div>
                <div
                    v-if="width >= 1270"
                    class="relative flex-row flex-nowrap items-center justify-center lg:flex lg:justify-end lg:gap-[2.65vw] 3xl:gap-[50.8px]"
                >
                    <div
                        v-for="(item, index) in experienceData"
                        :key="item"
                        class="experience-slide experience-slide-desktop max-w-[500px] self-stretch lg:w-[22vw]"
                        :ref="
                            (el) => {
                                if (el && !experienceSlidesRefs.includes(el)) {
                                    experienceSlidesRefs.push(el);
                                }
                            }
                        "
                    >
                        <div
                            class="flex h-full w-full translate-y-full flex-col items-center justify-center lg:gap-[1.59vw] 3xl:gap-[30.8px]"
                        >
                            <div
                                data-no-parse="true"
                                class="flex w-full flex-1 items-center lg:justify-between lg:gap-[1.59vw] lg:px-[0.53vw] 3xl:gap-[30.8px] 3xl:px-[10.2px]"
                            >
                                <p
                                    class="slt-h4-t self-start"
                                    v-html="htmlPurify(item.name)"
                                ></p>
                                <BaseIconButton
                                    :isExternal="true"
                                    :href="item?.href"
                                    variant="secondary"
                                    :ariaLabel="
                                        $t('ariaLabel.index.experience')
                                    "
                                    :class="{
                                        'hidden lg:block': index !== 1,
                                    }"
                                    v-sendGAsignal="{
                                        event_category: 'home.experience',
                                        event_label: item?.description,
                                    }"
                                ></BaseIconButton>
                            </div>
                            <div class="relative w-full">
                                <div
                                    class="absolute left-[4%] top-[2%] h-[96%] w-[92%] overflow-hidden rounded-[6.1vw] lg:rounded-[1.06vw] 3xl:rounded-[20.3px]"
                                >
                                    <img
                                        width="486"
                                        height="1051"
                                        loading="lazy"
                                        class="w-full object-cover object-center"
                                        :src="
                                            imagesData.find(
                                                (image) =>
                                                    image.id ==
                                                    item?.image?.[0],
                                            )?.source_url
                                        "
                                        :alt="
                                            imagesData.find(
                                                (image) =>
                                                    image.id ==
                                                    item?.image?.[0],
                                            )?.alt_text
                                        "
                                    />
                                </div>
                                <a
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    :href="item?.href"
                                    class="relative w-full cursor-pointer"
                                    :aria-label="
                                        $t('ariaLabel.index.experience')
                                    "
                                    v-sendGAsignal="{
                                        event_category: 'home.experience',
                                        event_label: item?.description,
                                    }"
                                >
                                    <img
                                        width="269"
                                        height="552"
                                        loading="lazy"
                                        alt="立即體驗-手機外框"
                                        class="w-full [-webkit-user-drag:none]"
                                        src="~/assets/images/pages/index/experience-phone.png"
                                    />
                                </a>
                            </div>
                        </div>
                    </div>
                    <div
                        v-if="experienceData.length < 2"
                        class="experience-slide experience-slide-desktop pointer-events-none max-w-[500px] self-stretch opacity-0 lg:w-[22vw]"
                    >
                        <div
                            class="flex flex-col items-center justify-center lg:gap-[1.59vw] 3xl:gap-[30.8px]"
                        >
                            <div
                                data-no-parse="true"
                                class="flex w-full flex-1 items-center lg:justify-between lg:gap-[1.59vw] lg:px-[0.53vw] 3xl:gap-[30.8px] 3xl:px-[10.2px]"
                            >
                                <p
                                    class="slt-h4-t self-start"
                                    v-html="htmlPurify(experienceData[0]?.name)"
                                ></p>
                                <BaseIconButton
                                    :isLink="false"
                                    :isExternal="true"
                                    variant="secondary"
                                    class="hidden lg:block"
                                    :ariaLabel="
                                        $t('ariaLabel.index.experience')
                                    "
                                ></BaseIconButton>
                            </div>
                            <a
                                target="_blank"
                                rel="noopener noreferrer"
                                href="#"
                                class="pointer-events-none relative w-full cursor-pointer"
                                :aria-label="$t('ariaLabel.index.experience')"
                            >
                                <img
                                    width="269"
                                    height="552"
                                    loading="lazy"
                                    alt="立即體驗-手機外框"
                                    class="w-full [-webkit-user-drag:none]"
                                    src="~/assets/images/pages/index/experience-phone.png"
                                />
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!-- ESG -->
        <div
            id="esg"
            ref="esgRef"
            class="sticky top-0 w-full bg-base-bgGrey sm:min-h-[calc(var(--window-height)*2)]"
        >
            <div
                class="slt-container top-0 px-[4.07vw] py-[20.36vw] xs:px-[16px] xs:py-[80px] sm:sticky sm:h-[--window-height] sm:px-[5.21vw] sm:py-[calc(var(--window-height)*0.078)] lg:h-[--window-height] lg:px-[2.65vw] lg:py-[calc(var(--window-height)*0.1148)] 3xl:px-[50.8px]"
            >
                <div
                    class="relative flex w-full flex-col gap-[10.18vw] xs:gap-[40px] sm:h-full sm:justify-center lg:items-center lg:justify-center"
                >
                    <!-- Mobile Title & Subtitle -->
                    <div
                        v-if="width < 1270"
                        class="w-full text-center lg:hidden"
                    >
                        <div class="slt-h2 mb-[4.07vw] xs:mb-[16px]">
                            <BaseFormatText2
                                type="h2"
                                :isAutoRevert="false"
                                class="whitespace-pre"
                                ref="esgMobileTitleRef"
                                :text="content?.esg?.title"
                            />
                        </div>
                        <div class="slt-h6 text-base-black-60">
                            <BaseFormatText2
                                type="h3"
                                :isAutoRevert="false"
                                class="whitespace-pre"
                                ref="esgMobileSubtitleRef"
                                :text="content?.esg?.subtitle"
                            />
                        </div>
                    </div>
                    <div
                        id="esgEarthContainer"
                        ref="esgEarthContainerRef"
                        class="aspect-[1/1] w-full sm:h-[200px] sm:flex-[1_1_200px] sm:self-center lg:h-[38.36vw] lg:w-[38.36vw] lg:flex-[0_0_38.36vw] 3xl:h-[736.5px] 3xl:w-[736.5px] 3xl:flex-[0_0_736.5px]"
                    ></div>
                    <div
                        class="relative flex lg:absolute lg:w-full lg:flex-row lg:items-center lg:justify-between"
                    >
                        <div
                            v-if="width >= 1270"
                            class="hidden text-center lg:flex lg:w-[28vw] lg:flex-col lg:items-start lg:gap-[1.59vw] 3xl:w-[537.6px] 3xl:gap-[30.5px]"
                        >
                            <!-- Desktop Title & Subtitle -->
                            <div class="slt-h2 text-left" data-no-parse="true">
                                <BaseFormatText2
                                    type="h2"
                                    :isAutoRevert="false"
                                    class="whitespace-pre"
                                    ref="esgDesktopTitleRef"
                                    :text="content?.esg?.title"
                                />
                            </div>
                            <div
                                class="slt-h6 text-base-black-60 lg:text-left"
                                data-no-parse="true"
                            >
                                <BaseFormatText2
                                    type="h3"
                                    :isAutoRevert="false"
                                    class="whitespace-pre"
                                    ref="esgDesktopSubtitleRef"
                                    :text="content?.esg?.subtitle"
                                />
                            </div>
                        </div>
                        <div
                            class="flex flex-col items-center gap-[10.18vw] self-stretch xs:gap-[40px] lg:w-[24.6vw] lg:items-start lg:justify-center lg:gap-[1.59vw] 3xl:w-[472.4px] 3xl:gap-[30.5px]"
                        >
                            <div class="w-full text-center lg:text-left">
                                <p
                                    ref="esgContentRef"
                                    class="slt-h6 overflow-hidden text-base-black-40"
                                >
                                    <span
                                        v-for="text in content?.esg?.content"
                                        :key="text"
                                        v-html="htmlPurify(text)"
                                        class="--text block text-base-black-40"
                                    ></span>
                                </p>
                            </div>
                            <div
                                class="w-full sm:w-[37.6vw] sm:self-center lg:w-[auto] lg:self-start"
                            >
                                <BaseIconButton
                                    :isLink="false"
                                    variant="secondary"
                                    class="cursor-pointer"
                                    :ariaLabel="$t('ariaLabel.index.esg')"
                                    @click="
                                        openModal(content?.esg?.button?.link)
                                    "
                                    v-sendGAsignal="{
                                        event_category:
                                            content?.esg?.button?.category,
                                        event_label:
                                            content?.esg?.button?.label,
                                    }"
                                >
                                    <span>{{
                                        content?.esg?.button?.text
                                    }}</span>
                                </BaseIconButton>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.slt-transition-clip-path {
    transition: clip-path 0.8s cubic-bezier(0.83, 0, 0.17, 1);
}

.slt-transition-color {
    transition: color 0.5s 0.5s linear;
}

.slt-vertical-text {
    writing-mode: vertical-rl;
    text-orientation: mixed;
}

.slt-text-animation {
    @apply will-change-transform;
}

.slt-text-animation.title {
    @apply translate-y-full;
    transition: transform 0.3s cubic-bezier(0.5, 0.07, 0.45, 1.59);
}

.slt-text-animation.subtitle {
    @apply translate-y-full;
    transition: transform 0.5s 0.3s cubic-bezier(0, 0.54, 0.34, 1);
}

.slt-text-animation.arrow {
    @apply -translate-y-full;
    transition: transform 0.5s 0.6s cubic-bezier(0.77, 0, 0.175, 1);
}

.arrow svg:deep(path) {
    transition:
        fill 0.5s 0.5s linear,
        stroke 0.5s 0.5s linear;
}

.slt-text-animation.title.active,
.slt-text-animation.subtitle.active,
.slt-text-animation.arrow.active {
    @apply translate-y-0;
}

.slt-text-loopAnimation {
    animation: text-loop 1.8s 3s cubic-bezier(0.87, 0, 0.13, 1) infinite
        forwards;
}

@keyframes text-loop {
    0% {
        transform: translateY(0%);
    }
    40% {
        transform: translateY(calc(100% + 4px));
    }
    100% {
        transform: translateY(calc(100% + 4px));
    }
}

.slt-h3-tdd {
    @apply leading-[1.1];
}

.focus-swiper.swiper {
    width: 100%;
    overflow: visible;
}

.focus-swiper .swiper-wrapper {
    overflow: visible;
    align-items: center;
}

.focus-swiper .swiper-slide.swiper-slide-active {
    transform: translate3d(0, 0, 0) !important;
    z-index: 3;
}

.focus-swiper .swiper-slide.swiper-slide-prev {
    transform: translate3d(0%, 0, -1000px) scale(0.001) !important;
    z-index: 2;
}

.focus-swiper
    .swiper-slide[data-swiper-slide-index="0"]:not(.swiper-slide-active) {
    transform: translate3d(-158.7%, 0, -1800px);
    z-index: 0;
}

/* #mock-carousel-block {
    transition: transform 0.4s;
} */

#mock-carousel-block .focus-in-scroll,
.focus-in-scroll .focus-swiper .swiper-slide-active ~ .swiper-slide {
    border-radius: Min(61px, 3.17vw) !important;
    margin-right: Min(45px, 2.34vw) !important;
}

.focus-slide img {
    transition: transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

/* :deep(.slt-slide-in > div) {
    transform: translateY(0);
    transition-property: transform;
    transition-duration: 0.7s;
    transition-timing-function: cubic-bezier(0.21, 0.65, 0.62, 1.2);
} */

.btn__arrow {
    @apply absolute right-0 top-0;
    transition:
        transform 0.3s cubic-bezier(0.17, 0.67, 0.3, 1.33),
        opacity 0.3s cubic-bezier(0.17, 0.67, 0.3, 1.33);
}

.btn__arrow--hover {
    transform: translate3d(-100%, 0, 0);
}

.news-card {
    transition: transform 0.3s cubic-bezier(0.17, 0.67, 0.3, 1.33);
}

.news-card > * {
    transition: transform 0.3s cubic-bezier(0.17, 0.67, 0.3, 1.33);
}

.news-card:hover {
    @apply lg:scale-105;
}

.news-card:hover > * {
    @apply lg:scale-[0.9524];
}

.news-card:hover .btn__arrow--hover {
    transform: translate3d(0, 0, 0);
}

.news-card:hover .btn__arrow--main {
    transform: translate3d(100%, 0, 0);
}

.news-card:hover .btn__arrow--hover {
    transform: translate3d(0, 0, 0);
}

.slt-transition-globe {
    transition: transform 0.8s cubic-bezier(0.83, 0, 0.17, 1);
}

.slt-text-show {
    transition: opacity 0.3s linear;
}

.--text {
    transition: transform 0.7s cubic-bezier(0.77, 0, 0.175, 1);
}

/* @media screen and (min-width: 1270px) {
    :deep(.slt-slide-in > div) {
        transition-duration: 0.3s;
        transition-timing-function: cubic-bezier(0.2, 1, 0.81, 1.21);
    }
} */
</style>
